import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  SunIcon, MoonIcon, GlobeAltIcon, ArrowLeftIcon,
  ShieldCheckIcon, InformationCircleIcon, CogIcon,
  LockClosedIcon, ArchiveBoxIcon, UserCircleIcon,
  CakeIcon, ArrowPathIcon, BuildingOfficeIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

const PrivacyPolicy: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();

  const sections = [
    { icon: InformationCircleIcon, title: t('saas.legal.privacyIntroTitle'), text: t('saas.legal.privacyIntro') },
    { icon: ArchiveBoxIcon, title: t('saas.legal.privacyCollectTitle'), text: t('saas.legal.privacyCollect') },
    { icon: CogIcon, title: t('saas.legal.privacyUseTitle'), text: t('saas.legal.privacyUse') },
    { icon: LockClosedIcon, title: t('saas.legal.privacySecurityTitle'), text: t('saas.legal.privacySecurity') },
    { icon: ArchiveBoxIcon, title: t('saas.legal.privacyRetentionTitle'), text: t('saas.legal.privacyRetention') },
    { icon: UserCircleIcon, title: t('saas.legal.privacyRightsTitle'), text: t('saas.legal.privacyRights') },
    { icon: CakeIcon, title: t('saas.legal.privacyCookiesTitle'), text: t('saas.legal.privacyCookies') },
    { icon: ArrowPathIcon, title: t('saas.legal.privacyChangesTitle'), text: t('saas.legal.privacyChanges') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-gray-950 dark:via-slate-900 dark:to-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      {/* Theme and Language Selectors */}
      <div className="fixed top-4 right-4 z-20 flex items-center space-x-3">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
          aria-label={theme === 'light' ? t('auth.switchToDark') : t('auth.switchToLight')}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
          aria-label={language === 'hu' ? t('auth.switchToEnglish') : t('auth.switchToHungarian')}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/">
            <Logo className="h-16 mx-auto" showCompanyName={false} />
          </Link>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-cyan-500/30 mt-6 mb-4">
            <ShieldCheckIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
            {t('saas.legal.privacyTitle')}
          </h1>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {t('saas.legal.privacyIntroTitle')}
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/60 dark:border-gray-700/60 hover:shadow-md transition-shadow duration-200 overflow-hidden"
              >
                <div className="flex items-start p-5 sm:p-6">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center mr-4">
                    <Icon className="h-5 w-5 text-blue-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1.5">
                      <span className="text-gray-400 dark:text-gray-500 mr-2">{index + 1}.</span>
                      {section.title}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {section.text}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Company info card */}
        <div className="mt-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/60 dark:border-gray-700/60 p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center mr-4">
              <BuildingOfficeIcon className="h-5 w-5 text-blue-600 dark:text-cyan-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('saas.legal.companyTitle')}</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p className="font-semibold text-gray-900 dark:text-gray-200">{t('saas.legal.companyName')}</p>
                <p>{t('saas.legal.companyReg')}</p>
                <p>{t('saas.legal.companyTax')}</p>
                <p>{t('saas.legal.companyAddress')}</p>
              </div>
              <a
                href={t('saas.legal.fullDocsUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-sm text-blue-600 dark:text-cyan-400 hover:underline font-medium"
              >
                {t('saas.legal.viewFullDocs')}
                <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            {t('saas.legal.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 